import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, CheckCircle2, Loader2, Save, ShieldAlert, Trash2, UploadCloud } from 'lucide-react';
import ProviderSchedulingManager from '../../features/scheduling/ProviderSchedulingManager';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import { AVATAR_ALLOWED_TYPES, AVATAR_MAX_SOURCE_BYTES, optimizeAvatarFile } from '../../lib/avatarUpload';
import { SelectMenu } from '../../ui/SelectMenu';
import InitialAvatar from '../../components/InitialAvatar';

type Provider = {
  id: string;
  type: string;
  userId?: string | null;
  defaultLocationId?: string | null;
  firstName: string;
  lastName: string;
  title?: string | null;
  licenseNumber?: string | null;
  npi?: string | null;
  specialties: string[];
  languages: string[];
  telemedAvailable: boolean;
  allowOutsideWorkingSchedule: boolean;
  waitlistEnabled: boolean;
  avatarUrl?: string | null;
  signature?: string | null;
  active: boolean;
  linkedUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  } | null;
};

type LocationOption = {
  id: string;
  name: string;
  isPrimary: boolean;
};

type UserLinkOption = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  linkedProfileType?: 'PROVIDER' | 'STAFF' | null;
  linkedProviderProfileId?: string | null;
  linkedStaffProfileId?: string | null;
};

const PROVIDER_TYPES = ['Doctor', 'Nurse', 'Dentist', 'Psychologist', 'Nurse Practitioner', 'Physician Assistant', 'Therapist', 'Other'];

const EMPTY_PROVIDER: Provider = {
  id: '',
  type: PROVIDER_TYPES[0],
  firstName: '',
  lastName: '',
  userId: '',
  title: '',
  licenseNumber: '',
  npi: '',
  specialties: [],
  languages: [],
  telemedAvailable: false,
  allowOutsideWorkingSchedule: false,
  waitlistEnabled: false,
  defaultLocationId: '',
  avatarUrl: '',
  signature: '',
  active: true,
  linkedUser: null,
};

function imagePreviewUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/')) return trimmed;
  return '';
}

export default function SettingsProviderDetail() {
  const navigate = useNavigate();
  const { providerId = 'new' } = useParams();
  const isNew = providerId === 'new';
  const { fetchJson, fetchApi } = useApiClient();
  const { tr, t } = useI18n();

  const [form, setForm] = useState<Provider>(EMPTY_PROVIDER);
  const [userOptions, setUserOptions] = useState<UserLinkOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [signatureUploadError, setSignatureUploadError] = useState<string | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const providerTypeOptions = useMemo(() => PROVIDER_TYPES.map((type) => ({ value: type, label: tr(type) })), [tr]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const [users, locations, provider] = await Promise.all([
        fetchJson<UserLinkOption[]>('/users/link-options?kind=provider'),
        fetchJson<LocationOption[]>('/locations'),
        isNew ? Promise.resolve(null) : fetchJson<Provider>(`/providers/${providerId}`),
      ]);
      setUserOptions(Array.isArray(users) ? users : []);
      setLocationOptions(Array.isArray(locations) ? locations : []);
      setForm(provider ?? EMPTY_PROVIDER);
    } catch (err: any) {
      setFailure(err.message ?? tr('Failed to load provider details.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, isNew, providerId, tr]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const parseComma = (value: string) => value.split(',').map((entry) => entry.trim()).filter(Boolean);
  const avatarPreviewUrl = useMemo(() => imagePreviewUrl(form.avatarUrl || ''), [form.avatarUrl]);
  const signaturePreviewUrl = useMemo(() => imagePreviewUrl(form.signature || ''), [form.signature]);
  const selectableUserOptions = useMemo(
    () =>
      userOptions.filter((option) => {
        if (option.linkedProfileType === 'STAFF') return option.id === form.userId;
        if (option.linkedProfileType === 'PROVIDER') {
          if (option.id === form.userId) return true;
          return option.linkedProviderProfileId === form.id;
        }
        return true;
      }),
    [form.id, form.userId, userOptions],
  );

  function handleChange<K extends keyof Provider>(key: K, value: Provider[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  }

  function validate() {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = tr('First name is required');
    if (!form.lastName.trim()) next.lastName = tr('Last name is required');
    if (!form.type.trim()) next.type = tr('Type is required');
    return next;
  }

  async function handleSave() {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    setFailure(null);
    setBanner(null);
    try {
      const payload = {
        ...form,
        userId: form.userId?.trim() ? form.userId.trim() : null,
        defaultLocationId: form.defaultLocationId?.trim() ? form.defaultLocationId.trim() : null,
        specialties: (form.specialties || []).filter(Boolean),
        languages: (form.languages || []).filter(Boolean),
      };
      const path = isNew ? '/providers' : `/providers/${providerId}`;
      const res = await fetchApi(path, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Save failed (${res.status})`);
      }
      const saved = (await res.json()) as Provider;
      setForm(saved);
      setBanner(tr('Provider saved'));
      if (isNew) {
        navigate(`/settings/providers/${saved.id}`, { replace: true });
      } else {
        await loadPage();
      }
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to save provider.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!form.id || !window.confirm(tr('Delete this provider?'))) return;
    try {
      const res = await fetchApi(`/providers/${form.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      navigate('/settings/providers');
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to delete provider.'));
    }
  }

  async function uploadAvatarImage(file?: File) {
    if (!file) return;
    setAvatarUploadError(null);
    if (!AVATAR_ALLOWED_TYPES.includes(file.type as (typeof AVATAR_ALLOWED_TYPES)[number])) {
      setAvatarUploadError(tr('Avatar image must be a PNG, JPEG, or WEBP file.'));
      return;
    }
    if (file.size > AVATAR_MAX_SOURCE_BYTES) {
      setAvatarUploadError(tr('Avatar image must be 5MB or smaller.'));
      return;
    }
    setUploadingAvatar(true);
    try {
      const optimizedFile = await optimizeAvatarFile(file);
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: optimizedFile.name,
          contentType: optimizedFile.type,
          kind: 'provider-avatar',
        }),
      });
      const uploadResponse = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': optimizedFile.type },
        body: optimizedFile,
      });
      if (!uploadResponse.ok) throw new Error(`Upload failed (${uploadResponse.status})`);
      handleChange('avatarUrl', presign.url);
      setBanner(tr('Avatar uploaded. Save provider to persist.'));
    } catch (err: any) {
      setAvatarUploadError(err?.message || tr('Avatar upload failed.'));
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function uploadSignatureImage(file?: File) {
    if (!file) return;
    setSignatureUploadError(null);
    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setSignatureUploadError(tr('Signature image must be a PNG or JPEG file.'));
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setSignatureUploadError(tr('Signature image must be 3MB or smaller.'));
      return;
    }
    setUploadingSignature(true);
    try {
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          kind: 'doctor-signature',
        }),
      });
      const uploadResponse = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadResponse.ok) throw new Error(`Upload failed (${uploadResponse.status})`);
      handleChange('signature', presign.url);
      setBanner(tr('Signature uploaded. Save provider to persist.'));
    } catch (err: any) {
      setSignatureUploadError(err?.message || tr('Signature upload failed.'));
    } finally {
      setUploadingSignature(false);
    }
  }

  if (loading) {
    return (
        <div className="p-6 text-slate-500">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
          </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="sticky top-0 z-10 -mx-6 border-b border-slate-200/50 bg-slate-50/85 px-6 py-4 backdrop-blur-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/settings/providers')} className="rounded-full p-2 hover:bg-slate-200">
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-ink">
                {isNew ? tr('New Provider') : `${form.firstName || ''} ${form.lastName || ''}`.trim() || tr('Provider Profile')}
              </h1>
              <p className="text-sm text-slate-500">
                {tr('Manage provider details, linked access, signatures, and patient self-scheduling from one page.')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {banner ? <span className="text-sm font-medium text-emerald-600">{banner}</span> : null}
            {!isNew && form.id ? (
              <button
                type="button"
                onClick={() => void handleDelete()}
                className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-sm text-rose-700 hover:bg-rose-50"
              >
                <Trash2 size={16} /> {tr('Delete')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-lg shadow-ink/20 disabled:opacity-60"
            >
              <Save size={18} /> {saving ? tr('Saving...') : tr('Save Provider')}
            </button>
          </div>
        </div>
      </div>

      {failure ? (
        <div className="flex items-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <ShieldAlert size={16} /> {failure}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <section className="card p-5">
            <div className="flex items-start gap-4">
              <InitialAvatar name={`${form.firstName} ${form.lastName}`} src={avatarPreviewUrl} alt={`${form.firstName} ${form.lastName}`} className="h-20 w-20 rounded-2xl text-lg" />
              <div className="space-y-2">
                <div>
                  <div className="text-lg font-semibold text-ink">
                    {`${form.firstName || ''} ${form.lastName || ''}`.trim() || tr('New Provider')}
                  </div>
                  <div className="text-sm text-slate-500">
                    {form.title || form.type || tr('Provider')}
                    {form.title && form.type ? ` · ${form.type}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-medium">
                  <span className={`rounded-full px-2 py-1 ${form.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {form.active ? tr('Active') : tr('Inactive')}
                  </span>
                  <span className={`rounded-full px-2 py-1 ${form.telemedAvailable ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                    {form.telemedAvailable ? tr('Telemed enabled') : tr('Telemed off')}
                  </span>
                  <span className={`rounded-full px-2 py-1 ${form.allowOutsideWorkingSchedule ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    {form.allowOutsideWorkingSchedule ? tr('Outside-hours instant telemed on') : tr('Outside-hours instant telemed off')}
                  </span>
                  <span className={`rounded-full px-2 py-1 ${form.waitlistEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {form.waitlistEnabled ? tr('Waitlist enabled') : tr('Waitlist off')}
                  </span>
                  <span className={`rounded-full px-2 py-1 ${form.userId ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
                    {form.userId ? tr('Scheduling enabled') : tr('Link user for scheduling')}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="card space-y-3 p-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Linked user')}</p>
              <p className="mt-2 text-sm text-slate-700">
                {form.linkedUser
                  ? `${form.linkedUser.firstName} ${form.linkedUser.lastName} · ${form.linkedUser.email}`
                  : tr('No linked user yet')}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Specialties')}</p>
              <p className="mt-2 text-sm text-slate-700">{form.specialties.length ? form.specialties.join(', ') : tr('None')}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Default location')}</p>
              <p className="mt-2 text-sm text-slate-700">
                {locationOptions.find((location) => location.id === form.defaultLocationId)?.name || tr('None')}
              </p>
            </div>
          </section>
        </aside>

        <div className="space-y-6">
          <section className="card space-y-5 p-5">
            <div>
              <h2 className="text-lg font-semibold text-ink">{tr('Provider Details')}</h2>
              <p className="text-sm text-slate-500">{tr('Use this record as the single source of truth for provider identity, credentials, and linked access.')}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label={tr('Type')}
                required
                value={form.type}
                options={providerTypeOptions.map((option) => option.value)}
                optionLabels={providerTypeOptions}
                onChange={(value) => handleChange('type', value)}
                error={errors.type}
              />
              <label className="block space-y-1 text-sm">
                <span className="font-medium text-slate-600">{tr('Linked User (optional)')}</span>
                <select
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ink/10"
                  value={form.userId || ''}
                  onChange={(event) => {
                    const nextUserId = event.target.value;
                    const selected = selectableUserOptions.find((option) => option.id === nextUserId);
                    setForm((prev) => ({
                      ...prev,
                      userId: nextUserId,
                      linkedUser: selected
                        ? {
                            id: selected.id,
                            firstName: selected.firstName,
                            lastName: selected.lastName,
                            email: selected.email,
                            role: selected.role,
                          }
                        : null,
                      firstName: prev.firstName || selected?.firstName || prev.firstName,
                      lastName: prev.lastName || selected?.lastName || prev.lastName,
                    }));
                  }}
                  >
                  <option value="">{tr('No linked user')}</option>
                  {selectableUserOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.lastName}, {option.firstName} ({option.role}) - {option.email}
                    </option>
                  ))}
                </select>
              </label>
              <Input label={tr('First Name')} required value={form.firstName} error={errors.firstName} onChange={(value) => handleChange('firstName', value)} />
              <Input label={tr('Last Name')} required value={form.lastName} error={errors.lastName} onChange={(value) => handleChange('lastName', value)} />
              <Input label={tr('Title')} value={form.title || ''} onChange={(value) => handleChange('title', value)} />
              <Input label={tr('License #')} value={form.licenseNumber || ''} onChange={(value) => handleChange('licenseNumber', value)} />
              <Input label={tr('NPI')} value={form.npi || ''} onChange={(value) => handleChange('npi', value)} />
              <Select
                label={tr('Default Location')}
                value={form.defaultLocationId || ''}
                options={locationOptions.map((location) => location.id)}
                optionLabels={locationOptions.map((location) => ({
                  value: location.id,
                  label: `${location.name}${location.isPrimary ? ` (${tr('Primary')})` : ''}`,
                }))}
                onChange={(value) => handleChange('defaultLocationId', value)}
              />
              <Toggle label={tr('Telemedicine Available')} value={form.telemedAvailable} onChange={(value) => handleChange('telemedAvailable', value)} />
              <Toggle
                label={tr('Allow Instant Telemed Outside Working Hours')}
                value={form.allowOutsideWorkingSchedule}
                onChange={(value) => handleChange('allowOutsideWorkingSchedule', value)}
              />
              <Toggle label={tr('Provider Waitlist Enabled')} value={form.waitlistEnabled} onChange={(value) => handleChange('waitlistEnabled', value)} />
              <Toggle label={tr('Active Provider')} value={form.active} onChange={(value) => handleChange('active', value)} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input label={tr('Specialties (comma separated)')} value={(form.specialties || []).join(', ')} onChange={(value) => handleChange('specialties', parseComma(value))} />
              <Input label={tr('Languages (comma separated)')} value={(form.languages || []).join(', ')} onChange={(value) => handleChange('languages', parseComma(value))} />
              <Input label={tr('Profile Avatar URL')} value={form.avatarUrl || ''} onChange={(value) => handleChange('avatarUrl', value)} />
              <Input label={tr('Signature Block / Image URL')} value={form.signature || ''} onChange={(value) => handleChange('signature', value)} />
            </div>
          </section>

          <section className="card space-y-4 p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl bg-emerald-50 p-2 text-emerald-700">
                <CalendarDays size={18} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-ink">{tr('Waitlist')}</h2>
                <p className="text-sm text-slate-500">
                  {tr('When enabled, this provider can accept patient portal waitlist requests for same-day or near-term appointment openings.')}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <Toggle
                label={tr('Enable waitlist for this provider')}
                description={tr('Turn this on when the provider wants to accept patients waiting for short-notice openings.')}
                value={form.waitlistEnabled}
                onChange={(value) => handleChange('waitlistEnabled', value)}
              />
            </div>
          </section>

          <section className="card space-y-4 p-5">
            <div>
              <h2 className="text-lg font-semibold text-ink">{tr('Media & Signature')}</h2>
              <p className="text-sm text-slate-500">{tr('Upload or draw the provider assets used across the chart, telemed, and documents.')}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50">
                  {uploadingAvatar ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                  {uploadingAvatar ? tr('Uploading...') : tr('Upload Profile Avatar')}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg"
                    onChange={(event) => {
                      const selected = event.target.files?.[0];
                      event.currentTarget.value = '';
                      void uploadAvatarImage(selected);
                    }}
                  />
                </label>
                {avatarPreviewUrl ? (
                  <button
                    type="button"
                    onClick={() => handleChange('avatarUrl', '')}
                    className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 hover:bg-rose-100"
                  >
                    <Trash2 size={14} />
                    {tr('Remove')}
                  </button>
                ) : null}
              </div>
              {avatarUploadError ? <p className="text-xs text-rose-600">{avatarUploadError}</p> : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50">
                  {uploadingSignature ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                  {uploadingSignature ? tr('Uploading...') : tr('Upload Signature Image')}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg"
                    onChange={(event) => {
                      const selected = event.target.files?.[0];
                      event.currentTarget.value = '';
                      void uploadSignatureImage(selected);
                    }}
                  />
                </label>
                {signaturePreviewUrl ? (
                  <button
                    type="button"
                    onClick={() => handleChange('signature', '')}
                    className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 hover:bg-rose-100"
                  >
                    <Trash2 size={14} />
                    {tr('Remove')}
                  </button>
                ) : null}
              </div>
              <SignatureDrawPad
                tr={tr}
                onApply={(dataUrl) => {
                  handleChange('signature', dataUrl);
                  setSignatureUploadError(null);
                  setBanner(tr('Drawn signature applied. Save provider to persist.'));
                }}
                onError={(message) => setSignatureUploadError(message)}
              />
              {signatureUploadError ? <p className="text-xs text-rose-600">{signatureUploadError}</p> : null}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mt-0.5 rounded-xl bg-sky-50 p-2 text-sky-700">
                  <CalendarDays size={18} />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-ink">{tr('Scheduling')}</h2>
                  <p className="text-sm text-slate-500">
                    {tr('Recurring working hours, portal bookable windows, and date overrides now live inside the provider profile so staff can configure scheduling in context.')}
                </p>
              </div>
            </div>

            {form.userId ? (
              <ProviderSchedulingManager
                providerUserId={form.userId}
                providerName={`${form.firstName || ''} ${form.lastName || ''}`.trim() || tr('Provider')}
                providerTitle={form.title || form.type || null}
                defaultLocationId={form.defaultLocationId || null}
                locationOptions={locationOptions}
              />
            ) : (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                {tr('Link this provider to a user account before configuring portal-visible scheduling windows. The scheduling engine is attached to the linked user identity.')}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  required,
  error,
  }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="flex items-center gap-1 font-medium text-slate-600">
        {label}
        {required ? <span className="text-rose-600">*</span> : null}
      </span>
      <input
        className={`w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ink/10 ${error ? 'border-rose-300' : 'border-slate-200'}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  optionLabels,
  required,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  optionLabels: Array<{ value: string; label: string }>;
  required?: boolean;
  error?: string;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="flex items-center gap-1 font-medium text-slate-600">
        {label}
        {required ? <span className="text-rose-600">*</span> : null}
      </span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={label}
        triggerClassName={`w-full rounded-lg border bg-white px-3 py-2 text-sm ${error ? 'border-rose-300' : 'border-slate-200'}`}
        options={options.map((option) => ({
          value: option,
          label: optionLabels.find((entry) => entry.value === option)?.label ?? option,
        }))}
      />
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </label>
  );
}

function Toggle({
  label,
  value,
  onChange,
  description,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
      <input type="checkbox" className="mt-1 accent-ink" checked={value} onChange={(event) => onChange(event.target.checked)} />
      <div>
        <div className="font-medium text-slate-800">{label}</div>
        {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
      </div>
    </label>
  );
}

function SignatureDrawPad({
  tr,
  onApply,
  onError,
}: {
  tr: (phrase: string) => string;
  onApply: (dataUrl: string) => void;
  onError: (message: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(320, Math.floor(rect.width || 640));
    const height = 160;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    hasStrokeRef.current = false;
    lastPointRef.current = null;
  }, []);

  useEffect(() => {
    resetCanvas();
  }, [resetCanvas]);

  function pointerToCanvasPoint(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function drawSegment(from: { x: number; y: number }, to: { x: number; y: number }) {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }

  function handlePointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = pointerToCanvasPoint(event);
    if (!point) return;

    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    hasStrokeRef.current = true;
    lastPointRef.current = point;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.arc(point.x, point.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
  }

  function handlePointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const point = pointerToCanvasPoint(event);
    if (!point) return;
    const from = lastPointRef.current || point;
    drawSegment(from, point);
    lastPointRef.current = point;
  }

  function stopDrawing(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    drawingRef.current = false;
    lastPointRef.current = null;
  }

  function applyDrawing() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!hasStrokeRef.current) {
      onError(tr('Draw a signature first.'));
      return;
    }
    onApply(canvas.toDataURL('image/png'));
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-medium text-slate-600">{tr('Or draw signature live')}</p>
      <canvas
        ref={canvasRef}
        className="h-40 w-full touch-none cursor-crosshair rounded-md border border-slate-300 bg-white"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDrawing}
        onPointerCancel={stopDrawing}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={resetCanvas}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          {tr('Clear')}
        </button>
        <button
          type="button"
          onClick={applyDrawing}
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100"
        >
          <CheckCircle2 size={14} />
          {tr('Use Drawing')}
        </button>
      </div>
    </div>
  );
}
